import React, { useState } from 'react';
import { View, Text, Pressable, ScrollView, useWindowDimensions } from 'react-native';
import theme from '../../theme';
import CheckIcon from '@mui/icons-material/Check';
import HomeServiceVisual from './HomeServiceVisual';

const SERVICES = [
    {
    id: 'agents',
    title: 'Conversational agents',
    body: 'Ask a question in plain English. The agent queries your data directly and answers with the right chart or number.',
    bullets: [
      'No SQL and no dashboard has to exist first',
      'Answers cite the underlying query, nothing is a black box',
      'Inherits your existing data permissions',
    ],
    visual: {
      variant: 'chat',
      data: {
        question: 'Which channel drove the most signups last week?',
        answer: 'Google Ads — 412 signups last week (+18% w/w).',
      },
    },
  },
  {
    id: 'attribution',
    title: 'Attribution modelling',
    body: 'See exactly which channel, campaign, and touchpoint earned the conversion — modelled against your data, not a generic default.',
    bullets: [
      'First-touch, last-touch, and blended models, switch anytime',
      'Cross-channel view — paid, organic, email, and referral in one model',
      'Re-runs automatically as new conversion data lands',
    ],
    visual: {
      variant: 'bars',
      data: [
        { label: 'Google Ads', value: 34, color: theme.colors.chart1 },
        { label: 'Meta Ads', value: 26, color: theme.colors.chart3 },
        { label: 'Email', value: 22, color: theme.colors.chart2 },
        { label: 'Organic', value: 18, color: theme.colors.chart4 },
      ],
    },
  },
  {
    id: 'ga4',
    title: 'GA4 integrations',
    body: 'Connect GA4 once. Events, conversions, and audiences sync straight into the warehouse — no export jobs, no stale property scans.',
    bullets: [
      'Native GA4 event and conversion sync, no BigQuery export setup',
      'Audiences stay in sync for activation and reporting alike',
      'Historical backfill on first connect',
    ],
    visual: {
      variant: 'stat',
      data: { label: 'Conversions synced', value: '12,482', delta: '+9.4%', sparkline: [20, 26, 24, 30, 34, 38, 44] },
    },
  },
  {
    id: 'bi',
    title: 'Business intelligence',
    body: 'Reports and dashboards built on live warehouse data, not a spreadsheet someone forgot to update.',
    bullets: [
      'Shared dashboards for spend, pipeline, and revenue in one view',
      'Drill from a summary chart to the underlying rows',
      'Scheduled reports delivered where the team already works',
    ],
    visual: {
      variant: 'table',
      data: {
        columns: ['channel', 'spend', 'roas'],
        rows: [
          ['Google Ads', '$18.2k', '4.1x'],
          ['Meta Ads', '$9.6k', '3.4x'],
          ['Email', '$1.1k', '9.8x'],
        ],
      },
    },
  }
];

function ServiceTabs(props) {
  const active = props.active;
  const onChange = props.onChange;
  const isCompact = props.isCompact;

  const row = (
    <View
      style={{
        flexDirection: 'row',
        borderBottomWidth: theme.borderWidth.thin,
        borderBottomColor: theme.colors.borderDefault,
      }}
    >
      {SERVICES.map((s) => {
        const isActive = s.id === active;
        return (
          <Pressable
            key={s.id}
            onPress={() => onChange(s.id)}
            style={{
              paddingVertical: theme.spacing.space2 + 2,
              marginRight: isCompact ? theme.spacing.space4 : theme.spacing.space5,
              marginBottom: -theme.borderWidth.thin,
              borderBottomWidth: theme.borderWidth.thick,
              borderBottomColor: isActive ? theme.colors.accentPrimary : 'transparent',
            }}
          >
            <Text
              style={{
                fontFamily: theme.typography.fontBody,
                fontSize: isCompact ? theme.typography.size.sm : theme.typography.size.sm,
                fontWeight: isActive ? theme.typography.weight.semibold : theme.typography.weight.regular,
                color: isActive ? theme.colors.textPrimary : theme.colors.textSecondary,
                whiteSpace: 'nowrap',
              }}
            >
              {s.title}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );

  if (!isCompact) return row;

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginHorizontal: -theme.spacing.space5 }} contentContainerStyle={{ paddingHorizontal: theme.spacing.space5 }}>
      {row}
    </ScrollView>
  );
}

function ServicePanel(props) {
  const service = props.service;
  const isCompact = props.isCompact;

  return (
    <View
      style={{
        flexDirection: isCompact ? 'column' : 'row',
        alignItems: isCompact ? 'stretch' : 'center',
        gap: isCompact ? theme.spacing.space8 : theme.spacing.space16 - 8,
        paddingVertical: isCompact ? theme.spacing.space8 : theme.spacing.space10,
      }}
    >
      <View style={{ flex: isCompact ? undefined : 1, width: isCompact ? '100%' : undefined }}>
        <Text
          style={{
            fontFamily: theme.typography.fontDisplay,
            fontWeight: theme.typography.weight.regular,
            fontSize: theme.typography.size.xl,
            letterSpacing: theme.trackingPx(theme.typography.size.xl, 'tight'),
            color: theme.colors.textPrimary,
            marginBottom: theme.spacing.space4 - 2,
          }}
        >
          {service.title}
        </Text>
        <Text
          style={{
            fontFamily: theme.typography.fontBody,
            fontSize: theme.typography.size.md,
            lineHeight: theme.lineHeightPx(theme.typography.size.md, 'normal'),
            color: theme.colors.textSecondary,
            marginBottom: theme.spacing.space5,
            maxWidth: isCompact ? undefined : 460,
          }}
        >
          {service.body}
        </Text>
        <View style={{ gap: theme.spacing.space2 + 2 }}>
          {service.bullets.map((b, i) => (
            <View key={i} style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing.space2 + 2 }}>
              <CheckIcon style={{ fontSize: 16, color: theme.colors.accentPrimary, marginTop: 2 }} />
              <Text
                style={{
                  fontFamily: theme.typography.fontBody,
                  fontSize: theme.typography.size.sm,
                  color: theme.colors.textSecondary,
                  flex: 1,
                }}
              >
                {b}
              </Text>
            </View>
          ))}
        </View>
      </View>

      <View style={{ flex: isCompact ? undefined : 1, alignItems: 'center', width: isCompact ? '100%' : undefined }}>
        <HomeServiceVisual variant={service.visual?.variant} data={service.visual?.data} />
      </View>
    </View>
  );
}

export function HomeServices() {
  const [activeTab, setActiveTab] = useState(SERVICES[0].id);
  const activeService = SERVICES.find((s) => s.id === activeTab);
  const { width } = useWindowDimensions();
  const isCompact = width < 700;

  return (
    <View
      style={{
        paddingHorizontal: isCompact ? theme.spacing.space5 : theme.spacing.space12,
        paddingBottom: isCompact ? theme.spacing.space12 : theme.spacing.space16 + 6,
        maxWidth: 1240,
        alignSelf: 'center',
        width: '100%',
      }}
    >
      <Text
        style={{
          fontFamily: theme.typography.fontDisplay,
          fontWeight: theme.typography.weight.regular,
          fontSize: isCompact ? theme.typography.size.lg : theme.typography.size['2xl'],
          letterSpacing: theme.trackingPx(theme.typography.size['2xl'], 'tight'),
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.space8 - 4,
        }}
      >
        What you get
      </Text>

      <ServiceTabs active={activeTab} onChange={setActiveTab} isCompact={isCompact} />
      <ServicePanel service={activeService} isCompact={isCompact} />
    </View>
  );
}

export default HomeServices;
